import type { DataTableDimensions } from '@/components/UI/DataTableV2/DataTable.model';

export interface EditorPositionParams {
  column: number;
  row: number;
  pinnedColumnCount: number;
  dimensions: DataTableDimensions;
  rowHeaderWidth: number;
  stickyWidth: number;
  totalHeaderHeight: number;
  scrollLeft: number;
  scrollTop: number;
}

export interface EditorRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Computes the position of the floating single-cell editor within `.gridContainer`.
 *
 * The grid has a fixed header band (`totalHeaderHeight`) at the top and a
 * left sticky band (`stickyWidth`) that never scrolls horizontally. Pinned
 * columns live inside that sticky band laid out after the row headers, while
 * scrollable columns live in the `.mainGrid` container offset by the sticky
 * band and shifted by the horizontal scroll. Every cell scrolls vertically
 * with the body, so `scrollTop` is always subtracted from the vertical offset.
 */
export const computeEditorPosition = ({
  column,
  row,
  pinnedColumnCount,
  dimensions,
  rowHeaderWidth,
  stickyWidth,
  totalHeaderHeight,
  scrollLeft,
  scrollTop,
}: EditorPositionParams): EditorRect => {
  const { cellWidth, cellHeight } = dimensions;
  const isPinned = column < pinnedColumnCount;

  const left = isPinned
    ? rowHeaderWidth + column * cellWidth
    : stickyWidth + (column - pinnedColumnCount) * cellWidth - scrollLeft;

  const top = totalHeaderHeight + row * cellHeight - scrollTop;

  return { left, top, width: cellWidth, height: cellHeight };
};

/**
 * Reports whether the origin cell has moved at all from where the editor is
 * pinned. The editor stays fixed at `pinned`; the chip is shown as soon as the
 * cell's live position differs from it, i.e. after any scroll.
 */
export const hasCellDrifted = (pinned: EditorRect, current: EditorRect): boolean => {
  return current.left !== pinned.left || current.top !== pinned.top;
};
